import { Popover } from '@base-ui/react';
import type { RestaurantEventAddonTierOption } from '@shared/types/restaurantEvents';
import type { RestaurantEventTier } from '@shared/types/restaurants';
import { centsToWholeDollar } from '@shared/utils/currency';
import cx from 'classnames';
import { useMemo, useState, type ReactElement, type RefObject } from 'react';
import typography from '~styles/typography.scss';
import type { TicketTierFormItem } from '../RestaurantEventForm';
import styles from './TierSelectionPopover.scss';

export type TierOption = RestaurantEventAddonTierOption;

export function buildAddonTierOptions(
  ticketTiers: TicketTierFormItem[],
  savedEventTiers?: RestaurantEventTier[] | null,
): TierOption[] {
  return ticketTiers.map((tier, index) => {
    const saved = savedEventTiers?.[index];
    return {
      id: saved?.id ?? `preview-tier-${index}`,
      name: tier.name || saved?.name || `Tier ${index + 1}`,
      price: parseFloat(String(tier.price ?? '0')) || 0,
    };
  });
}

interface TierSelectionPopoverProps {
  ticketTiers: TicketTierFormItem[];
  savedEventTiers?: RestaurantEventTier[] | null;
  selectedTierIds: string[];
  onTierIdsChange: (tierIds: string[]) => void;
  trigger: (tierOptions: TierOption[]) => ReactElement;
  /** When set, the popover portals inside the nearest dialog (event modal) like other add-on menus. */
  layoutRef?: RefObject<HTMLElement | null>;
}

export const TierSelectionPopover = ({
  ticketTiers,
  savedEventTiers,
  selectedTierIds,
  onTierIdsChange,
  trigger,
  layoutRef,
}: TierSelectionPopoverProps) => {
  const tiers = useMemo(
    () => buildAddonTierOptions(ticketTiers, savedEventTiers),
    [ticketTiers, savedEventTiers],
  );

  const [isOpen, setIsOpen] = useState(false);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null,
  );

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      const dialog = layoutRef?.current?.closest('[role="dialog"]');
      setPortalContainer(dialog instanceof HTMLElement ? dialog : null);
    }
  };

  const handleToggleTier = (tierId: string) => {
    const next = new Set(selectedTierIds);
    if (next.has(tierId)) {
      next.delete(tierId);
    } else {
      next.add(tierId);
    }
    onTierIdsChange(Array.from(next));
  };

  if (tiers.length === 0) {
    return null;
  }

  return (
    <Popover.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Popover.Trigger render={trigger(tiers)} />
      <Popover.Portal container={portalContainer ?? undefined}>
        <Popover.Positioner
          collisionBoundary={portalContainer ?? document.documentElement}
          positionMethod="fixed"
          side="bottom"
          sideOffset={8}
          className={styles.positioner}
        >
          <Popover.Popup className={styles.popup}>
            <h3 className={cx(typography.h6, styles.header)}>
              Add Ticket Tiers
            </h3>
            <p className={cx(typography.t2, styles.helpText)}>
              If no tiers are selected, this add-on will be available to all
              tiers.
            </p>
            <ul className={styles.tierList}>
              {tiers.map((tier) => (
                <li key={tier.id} className={styles.tierItem}>
                  <label className={styles.tierLabel}>
                    <input
                      type="checkbox"
                      checked={selectedTierIds.includes(tier.id)}
                      onChange={() => handleToggleTier(tier.id)}
                      className={styles.checkbox}
                    />
                    <span className={cx(typography.h7m, styles.tierName)}>
                      {tier.name}
                    </span>
                    {/* tier.price is in dollars from the form, convert to cents for display */}
                    <span className={cx(typography.t1, styles.tierPrice)}>
                      {centsToWholeDollar(tier.price * 100)}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
};

export const getTierSelectionLabel = (selectedTierIds: string[]): string => {
  if (selectedTierIds.length === 0) {
    return 'Assign Ticket Tiers';
  }
  return `Assign Ticket Tiers (${selectedTierIds.length})`;
};
